import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useDeleteCookiesMutation } from "../services/api";

const AUTO_LOGOUT_MS = 10 * 60 * 1000; // 10 minutes

// Expanded event list + noisy events (mousemove, pointer, touch) will be debounced
const EVENTS = [
  "pointerdown", "pointerup", "pointermove", "pointercancel",
  "mousemove", "mousedown", "mouseup", "click", "dblclick", "contextmenu", "wheel",
  "touchstart", "touchend", "touchmove", "touchcancel",
  "keydown", "keyup", "input",
  "scroll", "resize", "focus", "blur",
] as const;

const DEBOUNCE_MS = 300;

const AutoLogout = () => {
  const { logout, isAuthenticated } = useAuth0();
  const [deleteCookies] = useDeleteCookiesMutation();

  const [timeLeft, setTimeLeft] = useState<number>(AUTO_LOGOUT_MS / 1000);

  // Separate refs for timeout & interval
  const timeoutRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);
  const deadlineRef = useRef<number | null>(null);
  const lastResetRef = useRef<number>(0);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const hardLogout = useCallback(async () => {
    try {
      // Clear client/server cookies *before* redirect
      await deleteCookies({}).unwrap();
    } catch (e) {
      // swallow errors—logout should still proceed
    } finally {
      logout({ logoutParams: { returnTo: window.location.origin } });
    }
  }, [deleteCookies, logout]);

  const scheduleTimers = useCallback(() => {
    clearTimers();

    // (Re)set absolute deadline
    deadlineRef.current = Date.now() + AUTO_LOGOUT_MS;

    // Timeout to trigger logout
    timeoutRef.current = window.setTimeout(() => {
      if (isAuthenticated) {
        hardLogout();
      }
    }, AUTO_LOGOUT_MS);

    // 1s interval to recompute remaining time
    intervalRef.current = window.setInterval(() => {
      if (!deadlineRef.current) return;
      const remainingMs = Math.max(0, deadlineRef.current - Date.now());
      setTimeLeft(Math.ceil(remainingMs / 1000));
      if (remainingMs <= 0) {
        clearTimers();
        if (isAuthenticated) hardLogout();
      }
    }, 1000);
  }, [clearTimers, hardLogout, isAuthenticated]);

  // Debounced reset function
  const resetTimer = useCallback(() => {
    const now = Date.now();
    if (now - lastResetRef.current < DEBOUNCE_MS) {
      return; // skip noisy events happening too quickly
    }
    lastResetRef.current = now;

    scheduleTimers();

    // Cross-tab broadcast
    try {
      localStorage.setItem("__pegasus_idle_ping__", String(now));
    } catch {
      /* ignore */
    }
  }, [scheduleTimers]);

  // Listen for user activity
  useEffect(() => {
    if (!isAuthenticated) {
      clearTimers();
      return;
    }

    // Start timers on mount/auth
    scheduleTimers();

    // User activity events
    EVENTS.forEach((evt) =>
      window.addEventListener(evt, resetTimer, { passive: true })
    );

    // Reset when tab becomes visible again
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        resetTimer();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Cross-tab sync
    const onStorage = (e: StorageEvent) => {
      if (e.key === "__pegasus_idle_ping__") {
        scheduleTimers();
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      EVENTS.forEach((evt) =>
        window.removeEventListener(evt, resetTimer)
      );
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("storage", onStorage);
      clearTimers();
    };
  }, [isAuthenticated, resetTimer, scheduleTimers, clearTimers]);

  return null;
};

export default AutoLogout;
